import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  RadioButtonChecked,
  RadioButtonUnchecked,
  Refresh,
} from '@mui/icons-material';

import { trpc } from '../../api/trpc';
import { SimpleTable } from '../../molecules/simple-table';

export type ModbusHost = {
  ipv4?: string;
  mac?: string;
  vendor?: string;
};

type Props = {
  /** Modbus port to scan for. Hosts without it open are filtered out server-side. */
  port?: number;
  /** MAC of the currently selected host */
  value?: string;
  /** called with the whole host, or undefined when the selection is cleared */
  onSelect: (host: ModbusHost | undefined) => void;
  /** the vendor is a useful brand hint when choosing an inverter type */
  showVendor?: boolean;
};

/**
 * Scans the network for devices with a Modbus port open and lets one be picked.
 *
 * Requires a managed network — `network.scanHosts` throws BAD_REQUEST otherwise.
 */
export const ModbusHostsScan = ({
  port,
  value,
  onSelect,
  showVendor,
}: Props) => {
  const { mutate, isLoading, data, isError } =
    trpc.network.scanHosts.useMutation();

  const handleScan = () => {
    mutate(port ? { port } : {});
  };

  return (
    <>
      {!isLoading && !data && (
        <Button onClick={handleScan} size="large" variant="contained">
          <FormattedMessage
            id="inverter_wizard.scan"
            defaultMessage="Najít střídač na síti"
          />
        </Button>
      )}

      {isLoading && (
        <Stack sx={{ alignItems: 'center', my: 2 }} gap={1.5}>
          <CircularProgress />
          <Typography color="primary">
            <FormattedMessage
              id="inverter_wizard.scanning"
              defaultMessage="Hledám střídač na síti"
            />
          </Typography>
        </Stack>
      )}

      {isError && (
        <Typography sx={{ my: 2, color: 'error.main' }}>
          <FormattedMessage
            id="network.scan.error"
            defaultMessage="Skenování sítě se nezdařilo"
          />
        </Typography>
      )}

      {data && (
        <SimpleTable
          firstLineBold
          lastColBold={false}
          firstColBold={false}
          tableSx={{
            '& tr td:first-child': { paddingLeft: 0 },
            '& tr td:last-child': { paddingRight: 0 },
          }}
          rows={[
            [
              'IP adresa',
              'MAC adresa',
              ...(showVendor ? ['Výrobce'] : []),
              <Tooltip
                title={
                  <FormattedMessage
                    id="inverter_wizard.scan"
                    defaultMessage="Najít střídač na síti"
                  />
                }
                key={3}
              >
                <IconButton onClick={handleScan}>
                  <Refresh />
                </IconButton>
              </Tooltip>,
            ],
            ...data.map((host) => {
              const { ipv4, mac, vendor } = host;
              const selected = Boolean(value) && value === mac;

              return [
                ipv4,
                mac,
                ...(showVendor ? [vendor ?? '---'] : []),
                <Box key={3}>
                  <Checkbox
                    icon={<RadioButtonUnchecked />}
                    checkedIcon={<RadioButtonChecked />}
                    checked={selected}
                    onChange={() => onSelect(selected ? undefined : host)}
                  />
                </Box>,
              ];
            }),
          ]}
          sx={{ py: 0, px: 0 }}
        />
      )}
    </>
  );
};
